// File: lib/DiamondsContext.tsx
"use client";

import { createContext, useContext, useEffect, useState, useCallback } from "react";

// ─── Event bus contract ──────────────────────────────────────
// Pool xanh (mặc định — backward compat với code cũ):
//   window.dispatchEvent(new CustomEvent("jb:diamonds-changed", {
//     detail: { delta: -30, reason: "streak_freeze" }
//   }))
//
// Pool hồng (mới — referral, KYC withdraw):
//   window.dispatchEvent(new CustomEvent("jb:diamonds-changed", {
//     detail: { delta: 5, pool: "pink", reason: "ref_bonus" }
//   }))
//
// Refetch tuyệt đối:
//   { absolute: 260 }                    // pool xanh
//   { absolute: 12, pool: "pink" }       // pool hồng

export type DiamondPool = "green" | "pink";

export interface DiamondsEventDetail {
  delta?: number;         // cộng/trừ vào giá trị hiện tại
  absolute?: number;      // set giá trị tuyệt đối (ưu tiên hơn delta)
  pool?: DiamondPool;     // default "green" để backward compat
  reason?: string;        // debug/analytics (optional)
}

interface DiamondsContextType {
  diamonds: number;                                              // pool xanh (tiêu được)
  pinkDiamonds: number;                                          // pool hồng (chỉ rút qua KYC)
  setDiamonds: (value: number) => void;
  setPinkDiamonds: (value: number) => void;
  addDiamonds: (delta: number, reason?: string) => void;         // broadcast event pool xanh
  addPinkDiamonds: (delta: number, reason?: string) => void;     // broadcast event pool hồng
}

const DiamondsContext = createContext<DiamondsContextType>({
  diamonds: 0,
  pinkDiamonds: 0,
  setDiamonds: () => {},
  setPinkDiamonds: () => {},
  addDiamonds: () => {},
  addPinkDiamonds: () => {},
});

export function useDiamonds() {
  return useContext(DiamondsContext);
}

export function DiamondsProvider({
  initialDiamonds,
  initialPinkDiamonds,
  children,
}: {
  initialDiamonds: number;
  initialPinkDiamonds: number;
  children: React.ReactNode;
}) {
  const [diamonds, setDiamondsState] = useState(initialDiamonds);
  const [pinkDiamonds, setPinkDiamondsState] = useState(initialPinkDiamonds);

  // Listen event bus — phân biệt pool theo detail.pool (default "green")
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<DiamondsEventDetail>).detail ?? {};
      const pool: DiamondPool = detail.pool ?? "green";
      const setter = pool === "pink" ? setPinkDiamondsState : setDiamondsState;

      if (typeof detail.absolute === "number") {
        setter(Math.max(0, detail.absolute));
      } else if (typeof detail.delta === "number") {
        setter(prev => Math.max(0, prev + detail.delta!));
      }
    };
    window.addEventListener("jb:diamonds-changed", onChange);
    return () => window.removeEventListener("jb:diamonds-changed", onChange);
  }, []);

  // Sync khi initialDiamonds thay đổi (vd navigate sang trang khác → layout fetch lại)
  useEffect(() => {
    setDiamondsState(initialDiamonds);
  }, [initialDiamonds]);

  useEffect(() => {
    setPinkDiamondsState(initialPinkDiamonds);
  }, [initialPinkDiamonds]);

  const setDiamonds = useCallback((value: number) => {
    setDiamondsState(Math.max(0, value));
    window.dispatchEvent(
      new CustomEvent("jb:diamonds-changed", { detail: { absolute: value, pool: "green" } })
    );
  }, []);

  const setPinkDiamonds = useCallback((value: number) => {
    setPinkDiamondsState(Math.max(0, value));
    window.dispatchEvent(
      new CustomEvent("jb:diamonds-changed", { detail: { absolute: value, pool: "pink" } })
    );
  }, []);

  const addDiamonds = useCallback((delta: number, reason?: string) => {
    window.dispatchEvent(
      new CustomEvent("jb:diamonds-changed", { detail: { delta, pool: "green", reason } })
    );
  }, []);

  const addPinkDiamonds = useCallback((delta: number, reason?: string) => {
    window.dispatchEvent(
      new CustomEvent("jb:diamonds-changed", { detail: { delta, pool: "pink", reason } })
    );
  }, []);

  return (
    <DiamondsContext.Provider
      value={{
        diamonds,
        pinkDiamonds,
        setDiamonds,
        setPinkDiamonds,
        addDiamonds,
        addPinkDiamonds,
      }}
    >
      {children}
    </DiamondsContext.Provider>
  );
}
